<template>
  <Teleport to="body">
    <div v-if="isOpen" class="vue-profile-modal-overlay" @click="closeModal" role="presentation">
      <div ref="modalRef" class="vue-profile-modal" @click.stop role="dialog" aria-modal="true" aria-labelledby="profile-modal-title" tabindex="-1">
      <button class="vue-profile-modal-close" @click="closeModal" aria-label="Cerrar modal">&times;</button>
      <h2 id="profile-modal-title" class="sr-only">{{ $t('profile.login_title') }}</h2>
      
      <div v-if="!isAuthenticated" class="profile-not-logged-in">
        <div class="profile-tabs">
          <button 
            type="button" 
            class="profile-tab-btn" 
            :class="{ active: activeTab === 'login' }"
            @click="switchTab('login')"
          >
            {{ $t('profile.login_title') }}
          </button>
          <button 
            type="button" 
            class="profile-tab-btn" 
            :class="{ active: activeTab === 'register' }"
            @click="switchTab('register')"
          >
            {{ $t('profile.create_account') }}
          </button>
        </div>
        
        <!-- Login Tab -->
        <div v-if="activeTab === 'login'" class="profile-tab-content">
          <form id="vue-login-form" class="profile-form" @submit.prevent="handleLogin">
            <div class="form-group">
              <label for="vue-login-email">{{ $t('profile.email') }}</label>
              <input
                id="vue-login-email"
                v-model="loginEmail"
                type="email"
                :placeholder="$t('profile.email_placeholder')"
                required
                :aria-invalid="loginError ? 'true' : undefined"
                :disabled="isLoading"
                autocomplete="email"
              />
            </div>
            <div class="form-group">
              <label for="vue-login-password">{{ $t('profile.password') }}</label>
              <div class="password-input">
                <input
                  id="vue-login-password"
                  v-model="loginPassword"
                  :type="showPassword ? 'text' : 'password'"
                  :placeholder="$t('profile.password_placeholder')"
                  required
                  :aria-invalid="loginError ? 'true' : undefined"
                  :disabled="isLoading"
                  autocomplete="current-password"
                />
                <button
                  type="button"
                  class="password-toggle"
                  :aria-label="showPassword ? $t('profile.hide_password') : $t('profile.show_password')"
                  @click="togglePasswordVisibility"
                >
                  <svg v-if="!showPassword" class="eye-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <svg v-else class="eye-slash-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                  <span class="toggle-label">{{ showPassword ? $t('profile.hide_password') : $t('profile.show_password') }}</span>
                </button>
                <span class="sr-only">{{ showPassword ? $t('profile.hide_password') : $t('profile.show_password') }}</span>
              </div>
            </div>
            <div v-if="loginError" class="error-message" role="alert" aria-live="assertive">{{ loginError }}</div>
            <button
              type="submit"
              class="btn-primary"
              :class="{ 'btn-success': loginSuccess }"
              :disabled="isLoading"
            >
              {{ loginSuccess ? $t('profile.success') : (isLoading ? $t('common.loading') : $t('profile.login')) }}
            </button>
          </form>

           <div class="profile-actions">
             <button class="btn-link" data-testid="switch-to-forgot" @click="switchTab('forgot')">
               {{ $t('profile.forgot_password') }}
             </button>
             <button class="btn-link" data-testid="switch-to-register" @click="switchTab('register')">
               {{ $t('profile.create_account') }}
             </button>
           </div>
        </div>

        <!-- Forgot Password Tab -->
        <div v-else-if="activeTab === 'forgot'" class="profile-tab-content">
          <p class="tab-description">{{ $t('profile.forgot_password_desc') }}</p>
          <div v-if="forgotSuccess" class="success-message">{{ $t('profile.forgot_password_sent') }}</div>
          <form v-else class="profile-form" @submit.prevent="handleForgotPassword">
            <div class="form-group">
              <label for="vue-forgot-email">{{ $t('profile.email') }}</label>
              <input
                id="vue-forgot-email"
                v-model="forgotEmail"
                type="email"
                :placeholder="$t('profile.email_placeholder')"
                required
                :disabled="isLoading"
                autocomplete="email"
              />
            </div>
            <div v-if="forgotError" class="error-message">{{ forgotError }}</div>
            <button type="submit" class="btn-primary" :disabled="isLoading">
              {{ isLoading ? $t('common.loading') : $t('profile.forgot_password_send') }}
            </button>
          </form>
          <div class="profile-actions">
            <button class="btn-link" @click="switchTab('login')">
              {{ $t('profile.forgot_password_back') }}
            </button>
          </div>
        </div>
        
        <!-- Register Tab -->
        <div v-else-if="activeTab === 'register'" class="profile-tab-content">
          <form id="vue-register-form" class="profile-form" @submit.prevent="handleRegister">
            <div class="form-group">
              <label for="vue-register-name">{{ $t('profile.name') }}</label>
              <input
                id="vue-register-name"
                v-model="registerName"
                type="text"
                required
                :disabled="isLoading"
              />
            </div>
            <div class="form-group">
              <label for="vue-register-email">{{ $t('profile.email') }}</label>
                <input 
                  id="vue-register-email" 
                  v-model="registerEmail" 
                  type="email" 
                  required 
                  :disabled="isLoading"
                />
            </div>
            <div class="form-group">
              <label for="vue-register-phone">{{ $t('profile.phone') }}</label>
                <input 
                  id="vue-register-phone" 
                  v-model="registerPhone" 
                  type="tel" 
                  :disabled="isLoading"
                />
            </div>
            <div class="form-group">
              <label for="vue-register-password">{{ $t('profile.password') }}</label>
              <div class="password-input">
                    <input
                      id="vue-register-password"
                      v-model="registerPassword"
                      :type="showPassword ? 'text' : 'password'"
                      required
                      :disabled="isLoading"
                    />
                <button 
                  type="button" 
                  class="password-toggle" 
                  :aria-label="showPassword ? $t('profile.hide_password') : $t('profile.show_password')"
                  @click="togglePasswordVisibility"
                >
                  <svg v-if="!showPassword" class="eye-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <svg v-else class="eye-slash-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                </button>
              </div>
              <div id="password-strength-bar" class="password-strength" :class="passwordStrength"></div>
              <div id="password-requirements" class="password-requirements">
                <div class="req" :class="{ met: passwordMeetsLength }">{{ $t('profile.password_length') }}</div>
                <div class="req" :class="{ met: passwordHasNumber }">{{ $t('profile.password_number') }}</div>
                <div class="req" :class="{ met: passwordHasUppercase }">{{ $t('profile.password_uppercase') }}</div>
              </div>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input 
                  id="vue-accept-terms" 
                  v-model="termsAccepted" 
                  type="checkbox" 
                  required 
                  :disabled="isLoading"
                />
                {{ $t('profile.accept_terms') }}
              </label>
            </div>
            <div v-if="registerError" class="error-message">{{ registerError }}</div>
            <button 
              type="submit" 
              class="btn-primary" 
              :disabled="isLoading || !isRegisterFormValid"
            >
              {{ isLoading ? $t('common.loading') : $t('profile.create_account') }}
            </button>
          </form>
          
          <div class="profile-actions">
            <button class="btn-link" @click="switchTab('login')">
              {{ $t('profile.already_have_account') }}
            </button>
          </div>
        </div>
      </div>
      
      <div v-else class="profile-logged-in">
        <div class="profile-header">
          <img v-if="userAvatar" :src="userAvatar" alt="Avatar" class="profile-avatar" />
          <div v-else class="profile-avatar-fallback">
            {{ userInitials }}
          </div>
          <div class="profile-info">
            <h3>{{ user?.name || 'Usuario' }}</h3>
            <p>{{ user?.email || '' }}</p>
          </div>
        </div>

        <div class="profile-edit-section">
          <button v-if="!isEditingProfile" class="btn-secondary btn-edit-profile" @click="isEditingProfile = true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Editar información personal
          </button>
          <form v-else class="profile-edit-form" @submit.prevent="saveProfile">
            <div class="form-group">
              <label for="edit-name">Nombre</label>
              <input id="edit-name" v-model="editName" type="text" required />
            </div>
            <div class="form-group">
              <label for="edit-phone">Teléfono</label>
              <input id="edit-phone" v-model="editPhone" type="tel" />
            </div>
            <div v-if="editProfileError" class="error-message">{{ editProfileError }}</div>
            <div class="profile-edit-actions">
              <button type="button" class="btn-secondary" @click="isEditingProfile = false">Cancelar</button>
              <button type="submit" class="btn-primary" :disabled="isLoading">Guardar</button>
            </div>
          </form>
        </div>

         <div class="profile-actions">
           <button class="btn-secondary" data-testid="open-order-history" @click="openOrderHistory">
             {{ $t('profile.order_history') }}
           </button>
           <button class="btn-danger" :disabled="isLoading" @click="handleLogout">
             {{ $t('profile.logout') }}
           </button>
         </div>
      </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useUserStore } from '../../stores/user-store';
import { useSessionStore } from '../../stores/session-store';
import { useNotifications } from '../../composables/use-notifications';

// Imports
import { requestJSON } from '../../core/http';
import { getActiveElement, isAttached } from '@shared/utils/focus';

// Props and emits
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  initialTab: {
    type: String as () => 'login' | 'register' | 'forgot',
    default: 'login'
  }
});

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'open-order-history'): void;
}>();

// Stores
const userStore = useUserStore();
const sessionStore = useSessionStore();

const isAuthenticated = computed(() => Boolean(userStore.isAuthenticated));
const user = computed(() => userStore.user);
const userAvatar = computed(() => String(userStore.user?.avatar || '').trim());
const userInitials = computed(() => {
  const name = String(userStore.user?.name || '').trim();
  if (!name) return 'U';
  return name
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
});

function closeModal() {
  emit('close');
}

function openOrderHistory() {
  emit('open-order-history');
}

async function handleLogout() {
  if (isLoading.value) return;
  isLoading.value = true;
  try {
    await userStore.logout();
    await sessionStore.clearSession?.();
    notifications.success('Sesión cerrada correctamente');
    emit('close');
  } catch (err: any) {
    notifications.error(err?.message || 'Error al cerrar sesión');
  } finally {
    isLoading.value = false;
  }
}

// Modal state
const activeTab = ref<'login' | 'register' | 'forgot'>('login');
const showPassword = ref(false);

const notifications = useNotifications();
const loginSuccess = ref(false);
const isLoading = ref(false);
const loginError = ref<string | null>(null);
const registerError = ref<string | null>(null);
// Use individual refs instead of reactive object to avoid potential issues
const loginEmail = ref('');
const loginPassword = ref('');
const registerName = ref('');
const registerEmail = ref('');
const registerPhone = ref('');
const registerPassword = ref('');
const termsAccepted = ref(false);
const forgotEmail = ref('');
const forgotError = ref<string | null>(null);
const forgotSuccess = ref(false);
const isEditingProfile = ref(false);
const editName = ref('');
const editPhone = ref('');
const editProfileError = ref<string | null>(null);

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

watch(isEditingProfile, (editing) => {
  if (editing && userStore.user) {
    editName.value = userStore.user.name || '';
    editPhone.value = userStore.user.phone || '';
    editProfileError.value = null;
  }
});

const saveProfile = async () => {
  editProfileError.value = null;
  isLoading.value = true;
  try {
    await requestJSON('/api/client-auth/me', {
      method: 'PUT',
      body: JSON.stringify({ name: editName.value.trim(), phone: editPhone.value.trim() }),
    });
    await userStore.checkAuth();
    isEditingProfile.value = false;
    notifications.success('Perfil actualizado correctamente');
  } catch (err: any) {
    editProfileError.value = err?.message || 'Error al actualizar el perfil';
  } finally {
    isLoading.value = false;
  }
};

const handleLogin = async () => {
  if (isLoading.value) return;
  
  const email = loginEmail.value.trim();
  const password = loginPassword.value;

  if (!email || !password) {
    loginError.value = !email && !password
      ? 'Por favor ingresa tu correo y contraseña'
      : (!email ? 'Por favor ingresa tu correo electrónico' : 'Por favor ingresa tu contraseña');
    return;
  }

  loginError.value = null;
  isLoading.value = true;
  
  try {
    const success = await userStore.login({
      email,
      password,
    });

    if (success) {
      loginSuccess.value = true;
      const displayName = userStore.user?.name || 'Usuario';
      notifications.success(`Bienvenido, ${displayName}`);
      // Close modal after successful login
      setTimeout(() => {
        closeModal();
      }, 500);
    } else {
      loginError.value = userStore.error || 'Credenciales inválidas';
    }
  } catch (error) {
    loginError.value = error instanceof Error ? error.message : 'Error al iniciar sesión';
  } finally {
    isLoading.value = false;
  }
};

const modalRef = ref<HTMLElement | null>(null);
let lastFocusedElement: HTMLElement | null = null;
let keydownHandler: ((event: KeyboardEvent) => void) | null = null;

const getFocusableElements = (): HTMLElement[] => {
  if (!modalRef.value) return [];
  return Array.from(
    modalRef.value.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1 && el.getAttribute('aria-hidden') !== 'true');
};

const handleTabKey = (e: KeyboardEvent) => {
  const elements = getFocusableElements();
  if (elements.length === 0) return;

  const first = elements[0];
  const last = elements[elements.length - 1];

  // If focus has escaped the modal or is outside it, bring it inside
  if (!modalRef.value?.contains(getActiveElement())) {
    e.preventDefault();
    if (e.shiftKey) {
      last.focus();
    } else {
      first.focus();
    }
    return;
  }

  if (e.shiftKey) {
    if (getActiveElement() === first) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (getActiveElement() === last) {
      e.preventDefault();
      first.focus();
    }
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!props.isOpen) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    closeModal();
  } else if (event.key === 'Tab') {
    handleTabKey(event);
  }
};

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      lastFocusedElement = getActiveElement();
      if (!keydownHandler) {
        keydownHandler = handleKeydown;
        window.addEventListener('keydown', keydownHandler);
      }
      setTimeout(() => {
        const elements = getFocusableElements();
        if (elements.length > 0) {
          elements[0].focus();
        } else if (modalRef.value) {
          modalRef.value.focus();
        }
      }, 50);
    } else {
      if (keydownHandler) {
        window.removeEventListener('keydown', keydownHandler);
        keydownHandler = null;
      }
      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function' && isAttached(lastFocusedElement)) {
        try {
          lastFocusedElement.focus();
        } catch {
          // ignore
        }
      }
      lastFocusedElement = null;
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (keydownHandler) {
    window.removeEventListener('keydown', keydownHandler);
    keydownHandler = null;
  }
  if (lastFocusedElement && typeof lastFocusedElement.focus === 'function' && isAttached(lastFocusedElement)) {
    try {
      lastFocusedElement.focus();
    } catch {
      // ignore
    }
  }
});

watch(
  () => [props.isOpen, props.initialTab] as const,
  ([isOpen, initialTab]) => {
    if (!isOpen) return;
    if (initialTab === 'register') activeTab.value = 'register';
    else if (initialTab === 'forgot') activeTab.value = 'forgot';
    else activeTab.value = 'login';
    loginError.value = null;
    registerError.value = null;
    forgotError.value = null;
    forgotSuccess.value = false;
  },
  { immediate: true },
);

const switchTab = (tab: 'login' | 'register' | 'forgot') => {
  activeTab.value = tab;
  loginError.value = null;
  registerError.value = null;
  forgotError.value = null;
  forgotSuccess.value = false;
};

const handleForgotPassword = async () => {
  if (isLoading.value) return;
  forgotError.value = null;
  isLoading.value = true;
  try {
    await requestJSON('/api/client-auth/forgot-password', {
      method: 'POST',
      body: { email: forgotEmail.value.trim().toLowerCase() },
    });
    forgotSuccess.value = true;
  } catch (err) {
    forgotError.value = err instanceof Error ? err.message : 'Error al enviar el enlace';
  } finally {
    isLoading.value = false;
  }
};

const passwordMeetsLength = computed(() => registerPassword.value.length >= 8);
const passwordHasNumber = computed(() => /\d/.test(registerPassword.value));
const passwordHasUppercase = computed(() => /[A-Z]/.test(registerPassword.value));

const passwordStrength = computed(() => {
  const p = registerPassword.value;
  if (!p) return '';
  let score = 0;
  if (passwordMeetsLength.value) score++;
  if (passwordHasNumber.value) score++;
  if (passwordHasUppercase.value) score++;
  
  if (score <= 1) return 'weak';
  if (score === 2) return 'medium';
  return 'strong';
});

const isPhoneValid = computed(() => {
  const digits = registerPhone.value.replace(/\D/g, '');
  return digits.length === 0 || digits.length >= 10;
});

const isRegisterFormValid = computed(() => {
  return (
    registerName.value.trim() !== '' &&
    registerEmail.value.trim() !== '' &&
    isPhoneValid.value &&
    passwordMeetsLength.value &&
    passwordHasNumber.value &&
    passwordHasUppercase.value &&
    termsAccepted.value
  );
});

const handleRegister = async () => {
  registerError.value = null;
  
  if (!registerName.value.trim()) {
    registerError.value = 'El nombre es obligatorio.';
    return;
  }
  if (!registerEmail.value.trim()) {
    registerError.value = 'El correo electrónico es obligatorio.';
    return;
  }
  if (!passwordMeetsLength.value) {
    registerError.value = 'La contraseña debe tener al menos 8 caracteres.';
    return;
  }
  if (!passwordHasNumber.value) {
    registerError.value = 'La contraseña debe incluir al menos un número.';
    return;
  }
  if (!passwordHasUppercase.value) {
    registerError.value = 'La contraseña debe incluir al menos una letra mayúscula.';
    return;
  }
  if (!isPhoneValid.value) {
    registerError.value = 'El número de teléfono debe tener 10 dígitos.';
    return;
  }
  if (!termsAccepted.value) {
    registerError.value = 'Debes aceptar los términos y condiciones.';
    return;
  }

  try {
    isLoading.value = true;
    
    const success = await userStore.register({
      name: registerName.value.trim(),
      email: registerEmail.value.trim(),
      password: registerPassword.value,
      phone: registerPhone.value.trim() || ''
    });
    
    if (!success) {
      registerError.value = userStore.error || 'Error en el registro';
    } else {
      closeModal();
    }
  } catch (error) {
    registerError.value = error instanceof Error ? error.message : 'Error en el registro';
  } finally {
    isLoading.value = false;
  }
};

// Reset forms when modal closes
watch(() => props.isOpen, (isOpen) => {
  if (!isOpen) {
    loginEmail.value = '';
    loginPassword.value = '';
    registerName.value = '';
    registerEmail.value = '';
    registerPhone.value = '';
    registerPassword.value = '';
    termsAccepted.value = false;
    loginError.value = null;
    registerError.value = null;
    forgotError.value = null;
    forgotEmail.value = '';
    forgotSuccess.value = false;
    activeTab.value = 'login';
    showPassword.value = false;
    loginSuccess.value = false;
  }
});
</script>

<style scoped>
.vue-profile-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 13050;
}

.vue-profile-modal {
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  z-index: 1;
}

.vue-profile-modal h2 {
  margin: 0;
  color: #0f172a;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.15;
}

.vue-profile-modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  font-size: 24px;
  color: #475569;
  cursor: pointer;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.vue-profile-modal-close:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.profile-tabs {
  display: flex;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 4px;
  gap: 4px;
  margin-top: 12px;
  margin-bottom: 16px;
}

.profile-tab-btn {
  flex: 1;
  padding: 10px 16px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-weight: 600;
  font-size: 15px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.profile-tab-btn.active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.profile-tab-btn:hover:not(.active) {
  color: #334155;
}

.profile-not-logged-in, .profile-logged-in {
  margin-top: 16px;
}

.profile-tab-content {
  margin-top: 12px;
}

.profile-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.vue-profile-modal .form-group label {
  font-weight: 600;
  color: #334155;
}

.vue-profile-modal .form-group input {
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 16px;
  color: #0f172a;
  background: #ffffff;
}

.password-input {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input input {
  width: 100%;
}

.password-toggle {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  color: #64748b;
  transition: color 0.2s ease;
}

.password-toggle:hover {
  color: #1e293b;
}

.password-toggle svg {
  width: 18px;
  height: 18px;
}

.password-toggle .toggle-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  margin-left: 0.25rem;
  white-space: nowrap;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.password-strength {
  height: 4px;
  margin-top: 8px;
  border-radius: 2px;
  background-color: #e2e8f0;
  transition: all 0.3s ease;
  width: 0%;
}

.password-strength.weak {
  background-color: #ef4444;
  width: 33%;
}

.password-strength.medium {
  background-color: #f59e0b;
  width: 66%;
}

.password-strength.strong {
  background-color: #10b981;
  width: 100%;
}

.password-requirements {
  margin-top: 8px;
  font-size: 14px;
  color: #666;
}

.req {
  opacity: 0.6;
}

.req.met {
  opacity: 1;
  color: #10b981;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.error-message {
  color: #ef4444;
  font-size: 14px;
  margin-top: 8px;
}

input[aria-invalid="true"] {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 1px #ef4444;
}

.success-message {
  background: #f0fdf4;
  border: 1px solid #86efac;
  color: #166534;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}

.tab-description {
  color: #64748b;
  font-size: 14px;
  margin: 0 0 16px;
  line-height: 1.5;
}

.profile-edit-section {
  padding: 0 1.5rem;
  margin-bottom: 0.5rem;
}

.btn-edit-profile {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.profile-edit-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.profile-edit-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.profile-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-success {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  animation: success-pulse 0.5s ease-in;
}

.btn-success:hover:not(:disabled) {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
}

@keyframes success-pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #4b5563;
}

.btn-danger {
  background-color: #ef4444;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-danger:hover:not(:disabled) {
  background-color: #dc2626;
}

.btn-link {
  background: none;
  border: none;
  color: #3b82f6;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  text-align: left;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.profile-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
}

.profile-avatar-fallback {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ea580c, #c2410c);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.profile-info h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.profile-info p {
  margin: 4px 0 0 0;
  color: #666;
}
</style>
